import React from "react";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import PropTypes from "prop-types";
import ReactCountryFlag from "react-country-flag";
import CustomCheckBox from "../../../components/CheckBox/index";
import trashIcon from "../../../assets/images/trash.svg";

const AccesTable = ({
  accesslists,
  paginationOnchange,
  paginationFisrt,
  paginationRows,
  hanleChangeUpdateAccess,
  handleBatchToggle,
  updatEntity,
  openDeleteModal,
  loading,
}) => {
  const isProcessChecked = (entityId, serviceId, processId) => {
    const match = updatEntity?.find(
      (x) =>
        x.entityId == entityId &&
        x.serviceId == serviceId &&
        x.processId == processId,
    );
    return match ? match.checked : true;
  };

  const isServiceChecked = (entity, service) => {
    return service.process_templates?.every((process) =>
      isProcessChecked(entity.id, service.id, process.id),
    );
  };

  const hasAnyServiceProcessChecked = (entity, service) => {
    return service.process_templates?.some((process) =>
      isProcessChecked(entity.id, service.id, process.id),
    );
  };

  const isEntityChecked = (entity) => {
    return entity.services?.every((service) =>
      isServiceChecked(entity, service),
    );
  };

  const hasAnyEntityProcessChecked = (entity) => {
    return entity.services?.some((service) =>
      hasAnyServiceProcessChecked(entity, service),
    );
  };

  const getCheckState = (allChecked, anyChecked) => {
    if (allChecked) return "checked";
    if (anyChecked) return "indeterminate";
    return "unchecked";
  };

  const toggleEntity = (entity, checked) => {
    const items = [];
    entity.services?.forEach((service) => {
      service.process_templates?.forEach((process) => {
        items.push({
          entityId: entity.id,
          serviceId: service.id,
          processId: process.id,
          checked,
        });
      });
    });
    handleBatchToggle(items);
  };

  const toggleService = (entity, service, checked) => {
    const items = [];
    service.process_templates?.forEach((process) => {
      items.push({
        entityId: entity.id,
        serviceId: service.id,
        processId: process.id,
        checked,
      });
    });
    handleBatchToggle(items);
  };

  const convertToTreeNodes = (data) => {
    return data.map((entity) => {
      const entityChecked = isEntityChecked(entity);
      const entityHasAnyChecked = hasAnyEntityProcessChecked(entity);
      const entityState = getCheckState(entityChecked, entityHasAnyChecked);

      return {
        key: `entity-${entity.id}`,
        data: {
          name: (
            <span className="inline-flex align-items-center">
              <CustomCheckBox
                inputId={`checkbox-entity-${entity.id}`}
                checked={entityState !== "unchecked"}
                className={
                  entityState === "indeterminate" ? "p-indeterminate" : ""
                }
                onChange={() => toggleEntity(entity, entityState !== "checked")}
              />
              <label
                className="ml-2 inline-flex align-items-center"
                htmlFor={`checkbox-entity-${entity.id}`}
              >
                <ReactCountryFlag
                  countryCode={entity.country}
                  svg
                  className="mr-2"
                />
                {`${entity.name} (${entity.country})`}
              </label>
            </span>
          ),
          status: null,
          action: (
            <img
              src={trashIcon}
              alt="Delete"
              className="delete-access"
              onClick={() => openDeleteModal(entity)}
            />
          ),
        },
        children: entity.services?.map((service) => {
          const serviceChecked = isServiceChecked(entity, service);
          const serviceHasAnyChecked = hasAnyServiceProcessChecked(
            entity,
            service,
          );
          const serviceState = getCheckState(
            serviceChecked,
            serviceHasAnyChecked,
          );

          return {
            key: `service-${service.id}`,
            data: {
              name: (
                <span className="inline-flex align-items-center">
                  <CustomCheckBox
                    inputId={`checkbox-service-${entity.id}-${service.id}`}
                    checked={serviceState !== "unchecked"}
                    className={
                      serviceState === "indeterminate" ? "p-indeterminate" : ""
                    }
                    onChange={() =>
                      toggleService(entity, service, serviceState !== "checked")
                    }
                  />
                  <label
                    className="ml-2"
                    htmlFor={`checkbox-service-${entity.id}-${service.id}`}
                  >
                    {service.name}
                  </label>
                </span>
              ),
              status: null,
              action: null,
            },
            children: service.process_templates?.map((process) => {
              const isChecked = isProcessChecked(
                entity.id,
                service.id,
                process.id,
              );
              return {
                key: `process-${process.id}`,
                data: {
                  name: process.name,
                  status: (
                    <div className="flex  text-center checkbox-margin">
                      <CustomCheckBox
                        inputId={`checkbox-${process.id}`}
                        checked={isChecked}
                        onChange={(e) =>
                          hanleChangeUpdateAccess(
                            e.target.checked,
                            entity.id,
                            service.id,
                            process.id,
                          )
                        }
                      />
                    </div>
                  ),
                  action: null,
                },
              };
            }),
          };
        }),
      };
    });
  };

  const skeletonNodes = [
    {
      key: "skeleton-0",
      data: {
        name: (
          <div>
            <Skeleton width="60%" height="1.2rem" />
          </div>
        ),
        status: null,
        action: null,
      },
    },
  ];

  const treeNodes = loading
    ? skeletonNodes
    : convertToTreeNodes(accesslists || []);

  return (
    <div className="user-access-table">
      <TreeTable
        value={treeNodes}
        emptyMessage="No results were found"
        responsiveLayout="scroll"
        paginator={!loading && accesslists?.length > 10}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        rowsPerPageOptions={[10, 20, 30]}
        first={paginationFisrt}
        rows={paginationRows}
        onPage={paginationOnchange}
      >
        <Column
          field="name"
          header="Entity"
          expander
          className="table-entity-column"
        />
        <Column
          field="status"
          header="Status"
          headerClassName="table-status-header"
        />
        <Column field="action" />
      </TreeTable>
    </div>
  );
};

AccesTable.propTypes = {
  accesslists: PropTypes.array,
  paginationOnchange: PropTypes.func,
  paginationFisrt: PropTypes.number,
  paginationRows: PropTypes.number,
  hanleChangeUpdateAccess: PropTypes.func,
  handleBatchToggle: PropTypes.func,
  updatEntity: PropTypes.array,
  openDeleteModal: PropTypes.func,
  loading: PropTypes.bool,
};

export default AccesTable;
